import { HubConnectionBuilder, LogLevel, HttpTransportType, HubConnectionState } from '@microsoft/signalr';
import { Severity } from '@tc/logger';
import { ReplaySubject, combineLatest, from, of, map, distinctUntilChanged, withLatestFrom, switchMap as switchMap$1, first as first$1, Observable, filter, BehaviorSubject, share, catchError } from 'rxjs';
import { first, switchMap, tap, map as map$1 } from 'rxjs/operators';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, Injector, runInInjectionContext, makeEnvironmentProviders, NgModule } from '@angular/core';
import { createActionGroup, props, createSelector, Store, createReducer, on, provideStore, provideState } from '@ngrx/store';
import { unsubscribe } from '@tc/foundation/utilities';
import { Actions, createEffect, ofType, provideEffects } from '@ngrx/effects';

const WEB_SOCKET = 'WebSocket';
const MOZ_WEB_SOCKET = 'MozWebSocket';
class LiveUpdateHub {
    get Connection() {
        return this.connection;
    }
    constructor(envService, loggingService, groupName, hubConfigurationName, token$) {
        this.envService = envService;
        this.loggingService = loggingService;
        this.groupName = groupName;
        this.hubConfigurationName = hubConfigurationName;
        this.token$ = token$;
        this.isConnectedSubject = new ReplaySubject(1);
        this.isConnected$ = this.isConnectedSubject.asObservable();
        this.hubUpdatedSubject = new ReplaySubject(1);
        this.hubUpdated$ = this.hubUpdatedSubject.asObservable();
        this.connectionBuilder = new HubConnectionBuilder();
        this.initializeHub();
    }
    dispose() {
        this.stopListener();
    }
    initializeHub() {
        if (!this.isWebSocketCompatible()) {
            return;
        }
        combineLatest([this.envService.configuration$, this.token$])
            .pipe(first(), switchMap(([configuration, token]) => {
            this.hubConfiguration =
                configuration.liveUpdateGroupConfigurations
                    .find((c) => c.groupName === this.groupName)
                    .hubConfigurations.find((hc) => hc.configurationName ===
                    this.hubConfigurationName);
            return this.configureHub(configuration, token);
        }), tap((canStart) => {
            if (canStart) {
                this.startListener();
            }
        }))
            .subscribe();
    }
    sendMessage(command, message) {
        if (this.connection == null)
            return;
        from(this.connection.invoke(command, message)).subscribe(() => {
            this.loggingService.log(`${this.hubConfigurationName}.${command}`, Severity.Debug, {
                message: `Message sent: ${command}: ${this.hubConfigurationName}`,
            });
        }, (error) => {
            this.loggingService.log(`${this.hubConfigurationName}.${command}`, Severity.Error, {
                message: `Error sending command: ${command}: ${this.hubConfigurationName} - ${JSON.stringify(error)}`,
            });
        });
    }
    isWebSocketCompatible() {
        const socket = window[WEB_SOCKET] || window[MOZ_WEB_SOCKET];
        if (socket && socket.OPEN) {
            return true;
        }
        this.loggingService.log(this.hubConfigurationName, Severity.Warning, {
            message: `The browser does not support [WebSockets].`,
        });
        return false;
    }
    configureHub(configuration, token) {
        if (this.hubConfiguration != null &&
            (!this.hubConfiguration.requiresAuthentication || token)) {
            this.buildConnection(configuration, token);
            return of(true);
        }
        return of(false);
    }
    stopListener() {
        if (this.connection == null)
            return;
        this.connection.off(this.hubConfiguration.method);
        from(this.connection.stop()).subscribe(() => this.isConnectedSubject.next(false));
    }
    buildConnection(configuration, token) {
        if (this.hubConfiguration != null) {
            const url = `${configuration.liveUpdateGroupConfigurations.find((c) => c.groupName === this.groupName).apiUrl}/${this.hubConfiguration.endpoint}`;
            this.connection = token
                ? this.connectionBuilder
                    .withUrl(url, this.getSignalRConnectionOptions(token))
                    .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: () => 1000,
                })
                    .configureLogging(LogLevel.None)
                    .build()
                : this.connectionBuilder
                    .withUrl(url, this.getSignalRConnectionOptions())
                    .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: () => 1000,
                })
                    .configureLogging(LogLevel.None)
                    .build();
        }
    }
    startListener() {
        this.loggingService.log(`${this.hubConfigurationName}.startListener`, Severity.Debug, {
            message: 'Starting listener',
        });
        this.connection.on(this.hubConfiguration.method, (response) => this.hubUpdatedSubject.next(response));
        this.connection.onreconnected(() => this.isConnectedSubject.next(true));
        this.connection.onclose((error) => {
            this.isConnectedSubject.next(false);
            this.loggingService.log(`${this.hubConfigurationName}.startListener`, Severity.Warning, {
                message: `Hub Disconnected: ${error.message}`,
            });
        });
        from(this.connection.start()).subscribe(() => {
            this.isConnectedSubject.next(true);
            this.loggingService.log(`${this.hubConfigurationName}.startListener`, Severity.Debug, {
                message: `Listener Started: ${this.hubConfigurationName}`,
            });
        }, (error) => {
            this.isConnectedSubject.next(false);
            this.loggingService.log(`${this.hubConfigurationName}.startListener`, Severity.Error, {
                message: `Error starting listener: ${this.hubConfigurationName} - ${JSON.stringify(error)}`,
            });
        });
    }
    getSignalRConnectionOptions(token) {
        return token
            ? {
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
                accessTokenFactory: () => token,
            }
            : {
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
            };
    }
}

/* eslint-disable */
class MockLiveUpdateHub {
    constructor() {
        this.isConnected$ = of(true);
        this.hubUpdated$ = of(null);
    }
    initializeHub(callback) { }
    sendMessage(command, message, retries = 0) { }
    static { this.ɵfac = function MockLiveUpdateHub_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MockLiveUpdateHub)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockLiveUpdateHub, factory: MockLiveUpdateHub.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockLiveUpdateHub, [{
        type: Injectable
    }], null, null); })();

class MockHubConnection {
    constructor() {
        this.methodMap = new Map();
    }
    start() {
        return new Promise((resolve) => {
            resolve();
        });
    }
    stop() {
        return new Promise((resolve) => {
            resolve();
        });
    }
    stream(methodName, ...args) {
        return null;
    }
    send(methodName, ...args) {
        return new Promise((resolve) => {
            resolve();
        });
    }
    on(methodName, method) {
        this.methodMap.set(methodName, method);
    }
    off(methodName) {
        this.methodMap.delete(methodName);
    }
    onclose(callback) {
        callback();
    }
    invoke(methodName, ...params) {
        return new Promise((resolve) => {
            const invokableMethod = this.methodMap.get(methodName);
            resolve(invokableMethod(...params));
        });
    }
}

class MockHubConnectionBuilder {
    constructor() {
        this.connection = new MockHubConnection();
    }
    configureLogging(logLevelOrLogger) {
        return this;
    }
    withUrl(url, transportTypeOrOptions) {
        return this;
    }
    withHubProtocol(protocol) {
        return this;
    }
    build() {
        return this.connection;
    }
    static { this.ɵfac = function MockHubConnectionBuilder_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MockHubConnectionBuilder)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockHubConnectionBuilder, factory: MockHubConnectionBuilder.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockHubConnectionBuilder, [{
        type: Injectable
    }], null, null); })();

class MockSignalRService {
    constructor() {
        this.LogLevel = LogLevel;
        this.builderMock = new MockHubConnectionBuilder();
    }
    HubConnectionBuilder() {
        return this.builderMock;
    }
    static { this.ɵfac = function MockSignalRService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MockSignalRService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockSignalRService, factory: MockSignalRService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockSignalRService, [{
        type: Injectable
    }], null, null); })();

const fromHubActions = createActionGroup({
    source: 'Live-Updates',
    events: {
        'Create Hub': props(),
        'Hub Created': props(),
        Connect: props(),
        Connected: props(),
        'Connection Error': props(),
        Disconnect: props(),
        Disconnected: props(),
        Reconnect: props(),
        Error: props(),
        Subscribe: props(),
        Unsubscribe: props(),
        'Event Subscribed': props(),
        'Event Unsubscribed': props(),
        Send: props(),
        'Message Sent': props(),
        Dispose: props(),
    },
});

function getUnauthorizedError() {
    return Error(`Not authorized for this endpoint`);
}
function getConnectionNotFoundError(hubName) {
    return Error(`No connection found for hub: '${hubName}`);
}
function getConnectionTimeoutError() {
    return Error('Timed out waiting to connect');
}
function getDisconnectTimeoutError() {
    return Error('Timed out waiting to disconnect');
}

const selectAllHubs = (state) => state.hubs;
const selectHubState = (hubName) => createSelector(selectAllHubs, (state) => state.hubs.find((hub) => hub.hubName === hubName));
const selectHubError = (hubName) => createSelector(selectHubState(hubName), (state) => state.error);
const selectHubSubscribedEvents = (hubName) => createSelector(selectHubState(hubName), (state) => state.subscribedEvents);
const selectHubEventObservable = (hubName, eventName) => createSelector(selectHubSubscribedEvents(hubName), (state) => state[eventName]);

const AUTHENTICATION_TOKEN = new InjectionToken('authToken', { factory: () => of('') });
const URL_TOKEN = new InjectionToken('url');

class ReactiveHub {
    /**
     * Observable indicating whether the hub has active subscriptions.
     *
     * @returns {Observable<boolean>} An observable that emits true if there are active subscriptions, false otherwise.
     */
    get hasSubscriptions$() {
        return this.subscribedEvents$.pipe(map((events) => events && Object.keys(events).length > 0));
    }
    /**
     * Creates an instance of ReactiveHub.
     *
     * @param {string} hubName - The name of the hub.
     * @param {Observable<IReactiveHubConfiguration>} configuration - The configuration for the hub.
     */
    constructor(hubName, configuration) {
        this.hubName = hubName;
        this.configuration = configuration;
        this.store = inject((Store));
        this.token = inject(AUTHENTICATION_TOKEN);
        this.hubState$ = this.store.select(selectHubState(this.hubName));
        this.error$ = this.store.select(selectHubError(this.hubName));
        this.subscribedEvents$ = this.store.select(selectHubSubscribedEvents(this.hubName));
        this.tokenSubscription = this.token
            .pipe(distinctUntilChanged(), withLatestFrom(inject(URL_TOKEN), configuration))
            .subscribe(([token, url, configuration]) => {
            this.store.dispatch(fromHubActions.disconnect({ hubName: this.hubName }));
            this.store.dispatch(fromHubActions.createHub({
                hubName,
                configuration,
                token,
                url,
            }));
        });
    }
    /**
     * Disposes the hub and unsubscribes from all subscriptions.
     */
    dispose() {
        this.store.dispatch(fromHubActions.dispose({ hubName: this.hubName }));
        unsubscribe(this.tokenSubscription);
    }
    /**
     * Subscribes to a hub event and dispatches an action when the event occurs.
     *
     * @param {string} eventName - The name of the event to subscribe to.
     * @returns {object} An object containing the observable for the event and a dispatch function to dispatch actions.
     */
    on(eventName) {
        const observable = this.start().pipe(switchMap$1(() => this.#innerStream(eventName)), first$1());
        const dispatcher = (actionFn) => {
            observable.subscribe((message) => {
                const action = actionFn(message);
                this.store.dispatch(action);
            });
        };
        return {
            observable,
            dispatch: dispatcher,
        };
    }
    /**
     * Sends a message to the hub.
     *
     * @param {string} methodName - The name of the method to invoke on the hub.
     */
    send(methodName) {
        this.store.dispatch(fromHubActions.send({ hubName: this.hubName, message: methodName }));
    }
    /**
     * Starts the hub connection.
     *
     * @returns {Observable<HubConnectionState>} An observable that emits the connection state of the hub.
     */
    start() {
        return this.configuration.pipe(distinctUntilChanged((previous, current) => previous.requiresAuthentication ===
            current.requiresAuthentication), switchMap$1(() => {
            return new Observable((observer) => {
                combineLatest([this.token, this.hubState$])
                    .pipe(filter(([, state]) => !!state &&
                    state?.connectionState ===
                        HubConnectionState.Disconnected), distinctUntilChanged(([previousToken], [currentToken]) => previousToken === currentToken))
                    .subscribe({
                    next: () => {
                        this.store.dispatch(fromHubActions.connect({
                            hubName: this.hubName,
                        }));
                        observer.next(HubConnectionState.Disconnected);
                    },
                });
                combineLatest([this.token, this.hubState$])
                    .pipe(filter(([, state]) => !!state &&
                    state.connectionState ===
                        HubConnectionState.Connected), distinctUntilChanged(([previousToken], [currentToken]) => previousToken === currentToken))
                    .subscribe({
                    next: () => observer.next(HubConnectionState.Connected),
                    error: (error) => {
                        observer.error(error);
                    },
                });
            });
        }));
    }
    /**
     * Stops the hub connection.
     *
     * @returns {Observable<void>} An observable that completes when the hub is disconnected.
     */
    stop() {
        return this.configuration.pipe(first$1(), switchMap$1(() => {
            return new Observable((observer) => {
                this.hasSubscriptions$
                    .pipe(filter((hasSubscriptions) => hasSubscriptions), switchMap$1(() => this.subscribedEvents$), first$1())
                    .subscribe((events) => {
                    for (const eventName of Object.keys(events)) {
                        this.store.dispatch(fromHubActions.unsubscribe({
                            hubName: this.hubName,
                            eventName,
                        }));
                    }
                });
                this.hasSubscriptions$.subscribe(() => {
                    this.store.dispatch(fromHubActions.disconnect({
                        hubName: this.hubName,
                    }));
                });
                this.hubState$
                    .pipe(filter((state) => state.connectionState ===
                    HubConnectionState.Disconnected), first$1())
                    .subscribe({
                    next: () => {
                        observer.next();
                        observer.complete();
                    },
                    error: () => {
                        observer.error(getDisconnectTimeoutError());
                    },
                });
            });
        }));
    }
    /**
     * Subscribes to a hub event and returns an observable for the event.
     *
     * @param {string} eventName - The name of the event to subscribe to.
     * @returns {object} An object containing the observable for the event and a dispatch function to dispatch actions.
     */
    stream(eventName) {
        const observable = this.#innerStream(eventName);
        const dispatcher = (actionFn) => {
            observable.subscribe((message) => {
                const action = actionFn(message);
                this.store.dispatch(action);
            });
        };
        return {
            observable,
            dispatch: dispatcher,
        };
    }
    /**
     * Internal method to create an observable for a hub event.
     *
     * @param {string} eventName - The name of the event to subscribe to.
     * @returns {Observable<any>} An observable that emits the event data.
     */
    #innerStream(eventName) {
        this.store.dispatch(fromHubActions.subscribe({ hubName: this.hubName, eventName }));
        return this.store
            .select(selectHubEventObservable(this.hubName, eventName))
            .pipe(first$1((obs$) => !!obs$), switchMap$1((obs$) => obs$.pipe(map((obs) => obs))));
    }
}

var FeatureKind;
(function (FeatureKind) {
    FeatureKind[FeatureKind["CreateHub"] = 0] = "CreateHub";
    FeatureKind[FeatureKind["WithUrl"] = 1] = "WithUrl";
    FeatureKind[FeatureKind["WithToken"] = 2] = "WithToken";
})(FeatureKind || (FeatureKind = {}));

function makeFeature(kind, providers) {
    return {
        kind,
        providers,
    };
}

function withUrl(urlFactory, environmentDependency) {
    return makeFeature(FeatureKind.WithUrl, [
        {
            provide: URL_TOKEN,
            useFactory: urlFactory,
            deps: [environmentDependency],
        },
    ]);
}

function withToken(tokenFactory, environmentDependency) {
    return makeFeature(FeatureKind.WithToken, [
        {
            provide: AUTHENTICATION_TOKEN,
            useFactory: tokenFactory,
            deps: [environmentDependency],
        },
    ]);
}

/**
 * Creates a new hub feature.
 *
 * @param {string} hubName - The name of the hub to create.
 * @param {Observable<IReactiveHubConfiguration>} config - The configuration for the hub.
 * @returns {IFeature<FeatureKind.CreateHub>} The created hub feature.
 */
function createHub(hubName, config) {
    if (!hubName) {
        throw new Error('A hub name is required to create a hub.');
    }
    if (!config) {
        throw new Error('A configuration is required to create a hub.');
    }
    return makeFeature(FeatureKind.CreateHub, [
        {
            provide: hubs.get(hubName),
            useFactory: () => new ReactiveHub(hubName, config),
            deps: [AUTHENTICATION_TOKEN, (Store)],
        },
    ]);
}

class HubService {
    constructor() {
        this.#store = inject(Store);
        this.#connections = {};
        this.#connectionsSubject = new BehaviorSubject({});
        this.connections$ = this.#connectionsSubject.asObservable();
        this.observables = {};
    }
    #store;
    #connections;
    #connectionsSubject;
    createHubConnection(hubName, token, url) {
        const builder = new HubConnectionBuilder();
        const httpOptions = {
            transport: HttpTransportType.WebSockets,
            skipNegotiation: true,
        };
        if (token) {
            httpOptions.accessTokenFactory = () => token;
        }
        const connection = builder
            .withUrl(`${url}/${hubName}`, httpOptions)
            .withAutomaticReconnect()
            .configureLogging(LogLevel.None)
            .build();
        connection.on('disconnected', () => {
            this.#store.dispatch(fromHubActions.disconnected({ hubName }));
        });
        connection.on('error', (error) => {
            this.#store.dispatch(fromHubActions.connectionError({ hubName, error }));
        });
        this.#connections[hubName] = connection;
        this.#connectionsSubject.next(this.#connections);
        return connection;
    }
    createEventObservable(hubName, eventName) {
        const connection = this.#connections[hubName];
        const obs$ = new Observable((observer) => {
            const result = (data) => observer.next(data);
            connection.on(eventName, result);
            return () => {
                connection.off(eventName, result);
                this.#store.dispatch(fromHubActions.eventUnsubscribed({ hubName, eventName }));
            };
        }).pipe(share());
        if (!this.observables[hubName]) {
            this.observables[hubName] = {};
        }
        this.observables[hubName][eventName] = obs$;
        return obs$;
    }
    getHubConnection(hubName) {
        return this.connections$.pipe(map$1((hubs) => hubs[hubName]));
    }
    destroyHubConnection(hubName) {
        delete this.#connections[hubName];
        this.#connectionsSubject.next(this.#connections);
    }
    destroyEventObservable(hubName, eventName) {
        delete this.observables[hubName][eventName];
    }
    static { this.ɵfac = function HubService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || HubService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HubService, factory: HubService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HubService, [{
        type: Injectable
    }], null, null); })();

class HubProvider {
    constructor() {
        this.injector = inject(Injector);
    }
    initializeHub(hubName) {
        return runInInjectionContext(this.injector, () => inject(hubs.get(hubName)));
    }
}

const initialState = {
    hubs: [],
};
const hubsReducer = createReducer(initialState, on(fromHubActions.hubCreated, (state, { hub }) => ({
    ...state,
    hubs: [
        ...state.hubs,
        { ...hub, subscribedEvents: {}, messagesSent: [] },
    ],
})), on(fromHubActions.connected, (state, { hubName }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                connectionState: HubConnectionState.Connected,
            };
        }
        return h;
    }),
})), on(fromHubActions.connectionError, (state, { hubName, error }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                connectionState: HubConnectionState.Disconnected,
                error,
            };
        }
        return h;
    }),
})), on(fromHubActions.disconnect, (state, { hubName }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                connectionState: HubConnectionState.Disconnecting,
            };
        }
        return h;
    }),
})), on(fromHubActions.disconnected, (state, { hubName }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                connectionState: HubConnectionState.Disconnected,
            };
        }
        return h;
    }),
})), on(fromHubActions.eventSubscribed, (state, { hubName, eventName, observable$ }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                subscribedEvents: {
                    ...h.subscribedEvents,
                    [eventName]: observable$,
                },
            };
        }
        return h;
    }),
})), on(fromHubActions.reconnect, (state, { hubName }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                connectionState: HubConnectionState.Reconnecting,
            };
        }
        return h;
    }),
})), on(fromHubActions.error, (state, { hubName, error }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                error,
            };
        }
        return h;
    }),
})), on(fromHubActions.messageSent, (state, { hubName, message }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            return {
                ...h,
                messagesSent: [...h.messagesSent, message],
            };
        }
        return h;
    }),
})), on(fromHubActions.eventUnsubscribed, (state, { hubName, eventName }) => ({
    ...state,
    hubs: state.hubs.map((h) => {
        if (h.hubName === hubName) {
            const { [eventName]: removedEvent, ...remainingEvents } = h.subscribedEvents;
            return {
                ...h,
                subscribedEvents: remainingEvents,
            };
        }
        return h;
    }),
})));

class HubEffects {
    constructor() {
        this.#actions$ = inject(Actions);
        this.#hubService = inject(HubService);
        this.connect$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.connect, fromHubActions.reconnect), switchMap(({ hubName }) => this.#getHubConnection(hubName)), tap(({ connection }) => {
            if (connection.state === HubConnectionState.Disconnected) {
                connection.start();
            }
        }), catchError((_, caught) => caught.pipe(tap(({ hubName }) => fromHubActions.disconnected({ hubName })))), map$1(({ hubName }) => fromHubActions.connected({ hubName }))));
        this.createHub$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.createHub), filter((action) => !action.configuration.requiresAuthentication ||
            (action.configuration.requiresAuthentication &&
                !!action.token)), map$1((action) => {
            this.#hubService.createHubConnection(action.hubName, action.token, action.url);
            return fromHubActions.hubCreated({
                hub: {
                    hubName: action.hubName,
                    connectionState: HubConnectionState.Disconnected,
                },
            });
        })));
        this.disconnect$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.disconnect), switchMap(({ hubName }) => this.#getHubConnection(hubName)), filter(({ connection }) => !!connection), tap(({ connection }) => connection.stop()), map$1(({ hubName }) => fromHubActions.disconnected({ hubName }))));
        this.dispose$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.dispose), switchMap(({ hubName }) => this.#getHubConnection(hubName)), map$1(({ hubName, connection }) => {
            if (connection.state !== HubConnectionState.Disconnected) {
                connection.stop();
            }
            return { hubName };
        }), tap(({ hubName }) => this.#hubService.destroyHubConnection(hubName))), {
            dispatch: false,
        });
        this.send$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.send), switchMap(({ hubName, message }) => this.#getHubConnection(hubName).pipe(map$1((connection) => ({
            ...connection,
            message,
        })))), switchMap(({ hubName, connection, message }) => from(connection.send(message.methodName, message)).pipe(map$1(() => fromHubActions.messageSent({ hubName, message })), catchError((error) => of(fromHubActions.error({ hubName, error })))))));
        this.subscribe$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.subscribe), map$1(({ hubName, eventName }) => {
            const obs$ = this.#hubService.createEventObservable(hubName, eventName);
            return fromHubActions.eventSubscribed({
                hubName,
                eventName,
                observable$: obs$,
            });
        })));
        this.unsubscribe$ = createEffect(() => this.#actions$.pipe(ofType(fromHubActions.unsubscribe), map$1(({ hubName, eventName }) => {
            this.#hubService.destroyEventObservable(hubName, eventName);
            return fromHubActions.eventUnsubscribed({ hubName, eventName });
        })));
    }
    #actions$;
    #hubService;
    #getHubConnection(hubName) {
        return this.#hubService.getHubConnection(hubName).pipe(map$1((connection) => {
            return {
                hubName,
                connection,
            };
        }));
    }
    static { this.ɵfac = function HubEffects_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || HubEffects)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HubEffects, factory: HubEffects.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HubEffects, [{
        type: Injectable
    }], null, null); })();

/**
 * Provides reactive updates by configuring the necessary providers and state management.
 *
 * @param {...IFeature<FeatureKind>[]} features - The features to be included in the reactive updates.
 * @returns {EnvironmentProviders} The configured environment providers.
 * @throws {Error} If the required features (WithToken and WithUrl) are not provided.
 */
function provideReactiveUpdates(...features) {
    const featureKinds = new Set(features.map((f) => f.kind));
    if (!featureKinds.has(FeatureKind.WithToken)) {
        throw new Error('Missing withToken');
    }
    if (!featureKinds.has(FeatureKind.WithUrl)) {
        throw new Error('Missing withAccountInformation');
    }
    const providers = [HubService, HubProvider];
    features.forEach((feature) => {
        providers.push(...feature.providers);
    });
    return makeEnvironmentProviders([
        ...providers,
        provideStore(hubsReducer),
        provideState(hubsKey, hubsReducer),
        provideEffects([HubEffects]),
    ]);
}

const hubsKey = 'hubs';
const hubs = new Map();
class ReactiveUpdatesModule {
    /**
     * Builds the ReactiveUpdatesModule with the provided builder delegate.
     *
     * @param {function} builderDelegate - A function that configures the module builder.
     * @returns {ModuleWithProviders<ReactiveUpdatesModule>} The configured module with providers.
     */
    static build(builderDelegate) {
        const builder = new ReactiveUpdatesModuleBuilder();
        return builderDelegate(builder).build();
    }
    static { this.ɵfac = function ReactiveUpdatesModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ReactiveUpdatesModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ReactiveUpdatesModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ReactiveUpdatesModule, [{
        type: NgModule,
        args: [{
                imports: [],
                exports: [],
            }]
    }], null, null); })();
class ReactiveUpdatesModuleBuilder {
    constructor() {
        this._features = [];
    }
    /**
     * Gets the list of features configured by the builder.
     *
     * @returns {IFeature<FeatureKind>[]} The list of configured features.
     */
    get features() {
        return this._features;
    }
    /**
     * Adds a URL feature to the module builder.
     *
     * @param {function} environmentFactory - A factory function to create the environment observable.
     * @param {any} environmentDependency - The dependency required by the environment factory.
     * @returns {ReactiveUpdatesModuleBuilder} The updated module builder.
     */
    withUrl(environmentFactory, environmentDependency) {
        this._features.push(withUrl(environmentFactory, environmentDependency));
        return this;
    }
    /**
     * Adds a token feature to the module builder.
     *
     * @param {function} tokenFactory - A factory function to create the token observable.
     * @param {any} tokenServiceDependency - The dependency required by the token factory.
     * @returns {ReactiveUpdatesModuleBuilder} The updated module builder.
     */
    withToken(tokenFactory, tokenServiceDependency) {
        this._features.push(withToken(tokenFactory, tokenServiceDependency));
        return this;
    }
    /**
     * Adds a hub feature to the module builder.
     *
     * @param {string} hubName - The name of the hub to create.
     * @param {Observable<IReactiveHubConfiguration>} config - The configuration for the hub.
     * @returns {ReactiveUpdatesModuleBuilder} The updated module builder.
     */
    createHub(hubName, config) {
        hubs.set(hubName, new InjectionToken(hubName));
        this._features.push(createHub(hubName, config));
        return this;
    }
    /**
     * Builds the ReactiveUpdatesModule with the configured features.
     *
     * @returns {ModuleWithProviders<ReactiveUpdatesModule>} The configured module with providers.
     */
    build() {
        return {
            ngModule: ReactiveUpdatesModule,
            providers: [provideReactiveUpdates(...this._features)],
        };
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { HubProvider, LiveUpdateHub, MockHubConnection, MockHubConnectionBuilder, MockLiveUpdateHub, MockSignalRService, ReactiveHub, ReactiveUpdatesModule, ReactiveUpdatesModuleBuilder, hubs, hubsKey };

